import { bucketsWithAssigneesForDayQueryOptions } from "@/api/buckets/bucketsWithAssigneesForDayQueryOptions";
import { teamUsersQueryOptions } from "@/api/users/teamUsersQueryOptions";
import { Route as GanttRoute } from "@/routes/_gantt";
import { DRAWER_ID, useUIStore } from "@/stores/ui";
import { useQuery } from "@tanstack/react-query";
import { ErrorComponent as TanStackErrorComponent, createFileRoute, useRouter } from "@tanstack/react-router";

import { Button } from "@workspace/ui/components/button";
import { Drawer } from "@workspace/ui/components/drawer";

import { Dashboard } from "./-components/Dashboard";
import { CreateBucketForm } from "./-components/gantt-drawer/CreateBucketForm";
import { EditBucketForm } from "./-components/gantt-drawer/EditBucketForm";

export const Route = createFileRoute("/_gantt/")({
  loaderDeps: ({ search }) => ({ date: search.date }),
  component: Index,
  errorComponent: ErrorComponent,
  loader: ({ context: { queryClient }, deps: { date } }) => {
    void queryClient.prefetchQuery(bucketsWithAssigneesForDayQueryOptions(date));
    // Keep warm for 30s. No need to refetch if user immediately opens a drawer
    void queryClient.prefetchQuery(teamUsersQueryOptions({ staleTime: 30_000 }));
  },
});

function Index() {
  const activeDrawer = useUIStore((s) => s.activeDrawer);
  const closeDrawer = useUIStore((s) => s.closeDrawer);

  const isDrawerOpen = activeDrawer !== null;
  const editingBucketId = activeDrawer?.id === DRAWER_ID.GANTT_EDIT ? activeDrawer.bucketId : null;

  const { date } = GanttRoute.useSearch();
  const { data: buckets } = useQuery(bucketsWithAssigneesForDayQueryOptions(date));
  const editingBucket = buckets?.find((b) => b.id === editingBucketId);

  return (
    <>
      <div className={isDrawerOpen ? "w-7/10" : "w-full"}>
        <Dashboard />
      </div>
      {/* 
        Known vaul bug: With modal={false}, vaul still applies aria-hidden="true" to sibling elements,
        causing "Blocked aria-hidden on an element because its descendant retained focus" console warnings.
        
        Fix merged but not published to npm yet:
        - Issue: https://github.com/emilkowalski/vaul/issues/582
        - PR: https://github.com/emilkowalski/vaul/pull/580
        
        Remove this comment and verify fix works after upgrading vaul to version > 1.1.2
      */}
      <Drawer direction="right" open={isDrawerOpen} onOpenChange={closeDrawer} modal={false}>
        {activeDrawer?.id === DRAWER_ID.GANTT_CREATE && <CreateBucketForm />}
        {activeDrawer?.id === DRAWER_ID.GANTT_EDIT &&
          (editingBucket ? (
            <EditBucketForm bucket={editingBucket} />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">Bucket nicht gefunden</div>
          ))}
      </Drawer>
    </>
  );
}

function ErrorComponent({ error }: { error: Error }) {
  const router = useRouter();

  if (error.message) {
    return (
      <div className="p-10 flex flex-col items-center justify-center h-full gap-4">
        <p>Ein Fehler ist aufgetreten</p>
        <Button
          variant="secondary"
          onClick={() => {
            // If the error was the result of a route load, you should  call router.invalidate() instead of reset(), which will coordinate both a router reload and an error boundary reset.
            void router.invalidate();
          }}
        >
          Erneut versuchen
        </Button>
      </div>
    );
  }
  // Fallback to the default ErrorComponent
  return <TanStackErrorComponent error={error} />;
}
